import React, { useEffect, useState } from 'react';
import { Alert, Card, Skeleton, Table, Tag, Tooltip, theme } from 'antd';
import type { TableColumnsType } from 'antd';
import { LogLevel } from '@leedpulse-monorepo/enums';
import type { ILog } from '@leedpulse-monorepo/interfaces';
import getLogs from '../../../../domain/use_cases/logs/getLogs';
import { renderDate } from './callCells';
import RawDocumentModal from '../../@shared/RawDocumentModal';

const { useToken } = theme;

/** Longueur de l'aperçu du message. Le texte entier reste au survol, le
 *  document complet au clic. */
const PREVIEW_LENGTH = 110;

const ErrorLogsPanel: React.FC = () => {
  const { token } = useToken();
  const [logs, setLogs] = useState<ILog[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inspectedId, setInspectedId] = useState<string | null>(null);

  useEffect(() => {
    let isCurrent = true;
    // Pas de filtre de période : `level == error` + `timestamp desc` est le
    // seul couple indexé en production. Y ajouter une borne de date réclamerait
    // un index composite que la base n'a pas, et la requête échouerait.
    getLogs(undefined, { type: LogLevel.error })
      .then((res) => { if (isCurrent) setLogs(res.items); })
      .catch((e: Error) => { if (isCurrent) setError(e.message || 'Lecture impossible.'); });
    return () => { isCurrent = false; };
  }, []);

  const columns: TableColumnsType<ILog> = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      render: (timestamp: number) => renderDate(timestamp),
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (message: string) => {
        const text = String(message ?? '').replace(/\s+/g, ' ').trim();
        if (!text) return <span style={{ color: token.colorTextTertiary }}>-</span>;
        const preview = text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH)}…` : text;
        return <Tooltip title={text}><span>{preview}</span></Tooltip>;
      },
    },
  ];

  return (
    <Card title="Derniers logs en erreur">
      {error ? (
        <Alert type="error" showIcon message="Impossible de charger les logs" description={error} />
      ) : logs === null ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Table
          rowKey="id"
          dataSource={logs}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          scroll={{ x: true }}
          // Le document brut au clic : un log utile est justement celui dont
          // les champs annexes (payload, stack, ids) ne tiennent pas en colonne.
          onRow={(record) => ({
            onClick: () => setInspectedId(record.id),
            style: { cursor: 'pointer' },
          })}
        />
      )}

      <RawDocumentModal
        collection="Logs"
        documentId={inspectedId}
        onClose={() => setInspectedId(null)}
      />
    </Card>
  );
};

export default ErrorLogsPanel;
